import { Badge, BadgeProps } from "@/kit/ui/badge";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import {
  RewardFragment$data,
  RewardFragment$key,
} from "./__generated__/RewardFragment.graphql";
import { assertNever } from "@/utils/helpers";
import { FormattedMessage, FormattedNumber } from "react-intl";
import {
  Award03Icon,
  BookOpen02Icon,
  StickerCircleIcon,
  Trophy01Icon,
} from "@/icons";

const RewardFragment = graphql`
  fragment RewardFragment on Reward {
    id
    kind
    amount {
      start
      end
    }
  }
`;

interface Props {
  reward: RewardFragment$key;
}

function RewardLabel({ kind, amount }: RewardFragment$data) {
  switch (kind) {
    case "BADGE":
      return <FormattedMessage defaultMessage="Badge" />;
    case "LEARNING":
      return <FormattedMessage defaultMessage="Learning" />;
    case "MONEY":
      if (!amount) {
        return;
      }
      return (
        <FormattedNumber value={amount.end} style="currency" currency="EUR" />
      );
    case "SWAG":
      return <FormattedMessage defaultMessage="Swag" />;
    default:
      assertNever(kind);
  }
}

function RewardIcon({ kind }: RewardFragment$data) {
  switch (kind) {
    case "BADGE":
      return <Award03Icon className="h-3 w-3.5 text-info-500" />;
    case "LEARNING":
      return <BookOpen02Icon className="h-3 w-3.5 text-brand-500" />;
    case "MONEY":
      return <Trophy01Icon className="h-3 w-3.5 text-warning-500" />;
    case "SWAG":
      return <StickerCircleIcon className="h-3 w-3.5 text-success-500" />;
    default:
      assertNever(kind);
  }
}

const BADGE_VARIANT = Object.freeze({
  BADGE: "info",
  LEARNING: "secondary",
  MONEY: "warning",
  SWAG: "success",
} as const satisfies Record<
  RewardFragment$data["kind"],
  BadgeProps["variant"]
>);

export default function Reward(props: Props) {
  const reward = useFragment(RewardFragment, props.reward);

  return (
    <Badge
      hasPrefix
      className="pl-2.5 py-1 inline-flex items-center justify-center gap-1"
      variant={BADGE_VARIANT[reward.kind]}
    >
      <RewardIcon {...reward} />
      <div className="text-center justify-start text-sm font-medium leading-5">
        <RewardLabel {...reward} />
      </div>
    </Badge>
  );
}
